import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";
import { Skeleton } from "@mui/material";

export default function AddCoupon() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [couponName, setCouponName] = useState("");

    const [isLoading, setIsLoading] = useState(false);

    const { id } = useParams(); // coupon id from URL
    const navigate = useNavigate();
    const isEdit = Boolean(id); // true if /edit-coupon/:id

    const [formData, setFormData] = useState({
        coupon_code: "",
        discount_type: "",
        discount_value: "",
        start_date: "",
        end_date: "",
        usage_limit: "",
        status: "",
    });



    useEffect(() => {
        if (isEdit) {
            fetchCouponById();
        }
    }, [id]);


    const fetchCouponById = async () => {
        setIsLoading(true); // start loading
        try {
            const response = await axios.get(`${API_BASE_URL}/get-coupon-details/${id}`);
            console.log("Fetch coupon response:", response);

            const data = response.data.data;
            const couponName = data.coupon_code;
            setCouponName(couponName);

            if (response.data.success) {
                const formatDateForInput = (date) => {
                    if (!date) return "";
                    return new Date(date).toISOString().split("T")[0];
                };

                setFormData({
                    coupon_code: data.coupon_code || "",
                    discount_type: data.discount_type || "",
                    discount_value: data.discount_value || "",
                    start_date: formatDateForInput(data.start_date),
                    end_date: formatDateForInput(data.end_date),
                    usage_limit: data.usage_limit || "",
                    status: data.status?.toString() || "",
                });
            } else {
                toast.error("Failed to load coupon data.");
            }
        } catch (error) {
            console.error("Error fetching coupon:", error);
            toast.error("Error fetching coupon data");
        } finally {
            setIsLoading(false); // stop loading
        }
    };




    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Remove specific field error as user fixes input
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };


    const validateForm = () => {
        const newErrors = {};

        if (!formData.coupon_code.trim()) {
            newErrors.coupon_code = "Coupon code is required";
        } else if (!formData.discount_type.trim()) {
            newErrors.discount_type = "Discount type is required";
        } else if (!formData.discount_value || isNaN(formData.discount_value)) {
            newErrors.discount_value = "Valid discount value is required";
        } else if (!formData.start_date) {
            newErrors.start_date = "Start date is required";
        } else if (!formData.end_date) {
            newErrors.end_date = "End date is required";
        } else if (!formData.usage_limit || isNaN(formData.usage_limit)) {
            newErrors.usage_limit = "Valid usage limit is required";
        } else if (formData.status === "") {
            newErrors.status = "Status is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };




    const handleSubmit = async (e) => {
        e.preventDefault();

        const isValid = validateForm();
        if (!isValid) {
            toast.error("Please fix validation errors before submitting.");
            return;
        }
        // 🧹 Clean payload before sending
        const payload = {
            coupon_code: formData.coupon_code.trim(),
            discount_type: formData.discount_type.trim(),
            discount_value: Number(formData.discount_value),
            start_date: formData.start_date,
            end_date: formData.end_date,
            usage_limit: Number(formData.usage_limit),
            status: Number(formData.status), // convert "1"/"0" -> 1/0
        };

        setIsSubmitting(true);
        try {
            if (isEdit) {
                const response = await axios.put(`${API_BASE_URL}/update-coupon/${id}`, payload);
                if (response.data.success) {
                    toast.success("Coupon updated successfully!");
                    navigate("/cms/manage-coupons");
                } else {
                    toast.error("Failed to update coupon");
                }
            } else {
                const response = await axios.post(`${API_BASE_URL}/coupon`, payload);
                if (response.data.success) {
                    toast.success("Coupon added successfully!");
                    navigate("/cms/manage-coupons");
                } else {
                    toast.error("Failed to add coupon");
                }
            }
        } catch (error) {
            console.error("Error saving coupon:", error);
            toast.error("Error saving coupon");
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0 font-size-18">{isEdit ? "Edit" : "Add"} Coupons</h4>
                                </div>
                                <div className="card">
                                    <div className="card-body rounded-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h4 className="card-title mb-2">Coupons <b className="text-bold"> {couponName} </b> </h4>
                                        </div>

                                        {isLoading ? (
                                            <div className="p-4">
                                                <div className="row">
                                                    {Array.from({ length: 8 }).map((_, i) => (
                                                        <div className="col-md-6 mb-4" key={i}>
                                                            <Skeleton
                                                                variant="text"
                                                                height={20}
                                                                width="40%"
                                                                animation="wave"
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <Skeleton
                                                                variant="rectangular"
                                                                height={45}
                                                                animation="wave"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-4 d-flex gap-2">
                                                    <Skeleton
                                                        variant="rectangular"
                                                        height={40}
                                                        width={150}
                                                        animation="wave"
                                                        sx={{ borderRadius: 2 }}
                                                    />
                                                    <Skeleton
                                                        variant="rectangular"
                                                        height={40}
                                                        width={100}
                                                        animation="wave"
                                                        sx={{ borderRadius: 2 }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmit}>
                                                <div className="row">
                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Coupon Code</label>
                                                        <input
                                                            type="text"
                                                            name="coupon_code"
                                                            className={`form-control ${errors.coupon_code ? "is-invalid" : ""}`}
                                                            value={formData?.coupon_code}
                                                            onChange={handleChange}
                                                            placeholder="Enter Coupon Code"
                                                        />
                                                        {errors.coupon_code && (
                                                            <div className="invalid-feedback">{errors.coupon_code}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Discount Type</label>
                                                        <select
                                                            name="discount_type"
                                                            className={`form-select ${errors.discount_type ? "is-invalid" : ""}`}
                                                            value={formData.discount_type}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Choose...</option>
                                                            <option value="percentage">Percentage</option>
                                                            <option value="flat">Flat</option>
                                                        </select>
                                                        {errors.discount_type && (
                                                            <div className="invalid-feedback">{errors.discount_type}</div>
                                                        )}
                                                    </div>


                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Discount Value</label>
                                                        <input
                                                            type="text"
                                                            name="discount_value"
                                                            className={`form-control ${errors.discount_value ? "is-invalid" : ""}`}
                                                            value={formData?.discount_value}
                                                            onChange={handleChange}
                                                            placeholder="Enter Discount Value"
                                                        />
                                                        {errors.discount_value && (
                                                            <div className="invalid-feedback">{errors.discount_value}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Start Date</label>
                                                        <input
                                                            type="date"
                                                            name="start_date"
                                                            className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                                                            value={formData?.start_date}
                                                            onChange={handleChange}
                                                        />
                                                        {errors.start_date && (
                                                            <div className="invalid-feedback">{errors.start_date}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">End Date</label>
                                                        <input
                                                            type="date"
                                                            name="end_date"
                                                            className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
                                                            value={formData?.end_date}
                                                            onChange={handleChange}
                                                        />
                                                        {errors.end_date && (
                                                            <div className="invalid-feedback">{errors.end_date}</div>
                                                        )}
                                                    </div>

                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Usage Limit</label>
                                                        <input
                                                            type="text"
                                                            name="usage_limit"
                                                            className={`form-control ${errors.usage_limit ? "is-invalid" : ""}`}
                                                            value={formData?.usage_limit}
                                                            onChange={handleChange}
                                                            placeholder="Enter Usage Limit"
                                                        />
                                                        {errors.usage_limit && (
                                                            <div className="invalid-feedback">{errors.usage_limit}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mb-4">
                                                        <label className="form-label">Status</label>
                                                        <select
                                                            name="status"
                                                            className={`form-select ${errors.status ? "is-invalid" : ""}`}
                                                            value={formData.status}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Choose...</option>
                                                            <option value="1">Active</option>
                                                            <option value="0">Inactive</option>
                                                        </select>
                                                        {errors.status && (
                                                            <div className="invalid-feedback">{errors.status}</div>
                                                        )}
                                                    </div>


                                                </div>

                                                <div className="row">
                                                    <div className="col-12 mt-1">
                                                        <div className="d-flex align-items-center justify-content-start gap-2 ">
                                                            <div>
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary me-2 d-flex align-items-center gap-2"
                                                                    onClick={handleSubmit}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    {isSubmitting ? (
                                                                        <>
                                                                            <span
                                                                                className="spinner-border spinner-border-sm text-light"
                                                                                role="status"
                                                                                aria-hidden="true"
                                                                            ></span>
                                                                            {isEdit ? "Updating..." : "Adding..."}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {isEdit ? (
                                                                                <>
                                                                                    <i className="fa fa-pen" />
                                                                                    Update Coupon
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="fa fa-plus" />
                                                                                    Add Coupon
                                                                                </>
                                                                            )}
                                                                        </>

                                                                    )}
                                                                </button>
                                                            </div>


                                                            <div><Link to="/cms/manage-coupons" className="btn btn-secondary">
                                                                Cancel
                                                            </Link></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                        )}

                                        {/* <div className="row">
                                            <div className="col-12 mt-1">
                                                <div>
                                                    <button type="submit" className="btn btn-primary me-2">Add Coupon</button>
                                                    <Link className="btn btn-secondary" href="#">Cancel</Link>
                                                </div>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
